import { startTransition, useEffect, useState } from "react";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import type { LearnDeckSlot, LearnDeckState } from "@-/dev.improve.haniverse.lib.learn.deck";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";

const surfaceKey = (slot: LearnDeckSlot, face: "front" | "back") =>
  `${learnCardPresentationKey(slot.presentation)}:${face}`;

/** A slide or a quiz is one face: it never mounts a back. */
const hasBackFace = (slot: LearnDeckSlot) =>
  slot.item.kind !== "slide" && slot.item.kind !== "quiz";

/**
 * Mount one idle surface at a time; foreground actions can interrupt each transition.
 *
 * `minimal` (a memory warning while Learn is hidden behind another tab) keeps only the
 * current card's visible face: its back face, the next card and their GPU glyphs are released and
 * prepared again, one idle frame at a time, when Learn returns.
 */
export function useLearnSurfacePreloads(state: LearnDeckState, expanded: boolean, minimal = false) {
  // The flashcard queue decides what is preloaded: the card under the current one, then the next.
  const [successorEntry, bufferEntry] = state.queue;
  const successor = successorEntry?.kind === "card" ? successorEntry.slot : null;
  const buffer = bufferEntry?.kind === "card" ? bufferEntry.slot : null;
  const isPromoting =
    state.transition.phase === "committed" || state.transition.phase === "exiting";
  const desired = minimal
    ? [
        { face: "front" as const, slot: state.current },
        ...(state.face === "back" ? [{ face: "back" as const, slot: state.current }] : []),
      ]
    : [
        { face: "front" as const, slot: state.current },
        ...(hasBackFace(state.current) ? [{ face: "back" as const, slot: state.current }] : []),
        ...(successor === null ? [] : [{ face: "front" as const, slot: successor }]),
        ...(successor !== null && (expanded || isPromoting) && hasBackFace(successor)
          ? [{ face: "back" as const, slot: successor }]
          : []),
        ...(expanded && buffer !== null ? [{ face: "front" as const, slot: buffer }] : []),
      ];
  const [admitted, setAdmitted] = useState<string[]>([]);
  const allowedKeys = desired.map(({ face, slot }) => surfaceKey(slot, face));
  // An already mounted successor keeps its identity when promoted. Only the visible face is
  // urgent on cold entry; every speculative face waits for the preceding face's layout marker.
  const visibleKey = surfaceKey(state.current, state.face);
  const currentFrontKey = surfaceKey(state.current, "front");
  const urgentKeys = [currentFrontKey, visibleKey];
  // Promotion needs the successor's front. Its back is urgent too, so a flip soon after promotion
  // finds it rendered, even if a tab switch happens mid-action; nothing waits for it, and the idle
  // budget is restored once the successor becomes current.
  if (isPromoting && successor !== null) {
    urgentKeys.push(surfaceKey(successor, "front"));
    if (hasBackFace(successor)) {
      urgentKeys.push(surfaceKey(successor, "back"));
    }
  }
  const mountedKeys = admitted.filter((key) => allowedKeys.includes(key));
  for (const key of urgentKeys) {
    if (!mountedKeys.includes(key)) {
      mountedKeys.push(key);
    }
  }
  const next = desired.find(({ face, slot }) => !mountedKeys.includes(surfaceKey(slot, face)));
  const precedingReady =
    next === undefined ||
    desired.slice(0, desired.indexOf(next)).every(({ face, slot }) => slot.ready[face]);
  const nextKey = next === undefined ? null : surfaceKey(next.slot, next.face);
  const isExpandedPreload = next !== undefined && desired.indexOf(next) >= 3;
  const keysStamp = JSON.stringify(allowedKeys);
  const urgentStamp = JSON.stringify(urgentKeys);
  useEffect(() => {
    const allowed: string[] = JSON.parse(keysStamp);
    const urgent: string[] = JSON.parse(urgentStamp);
    setAdmitted((keys) => {
      if (keys.every((key) => allowed.includes(key)) && urgent.every((key) => keys.includes(key))) {
        return keys;
      }
      const nextKeys = keys.filter((key) => allowed.includes(key));
      for (const key of urgent) {
        if (!nextKeys.includes(key)) {
          nextKeys.push(key);
        }
      }
      return nextKeys;
    });
  }, [keysStamp, urgentStamp]);
  useEffect(() => {
    if (
      nextKey === null ||
      !precedingReady ||
      (state.transition.phase !== "idle" && state.transition.phase !== "committed")
    ) {
      return;
    }
    let cancelled = false;
    let handle: ReturnType<typeof requestIdleCallback> | undefined;
    let cancelFrames: (() => void) | undefined;
    const admit = (deadline: IdleDeadline) => {
      if (cancelled) {
        return;
      }
      if (deadline.timeRemaining() < 4) {
        handle = requestIdleCallback(admit);
        return;
      }
      startTransition(() => {
        const allowed: string[] = JSON.parse(keysStamp);
        setAdmitted((keys) =>
          cancelled
            ? keys
            : [...keys.filter((key) => allowed.includes(key) && key !== nextKey), nextKey],
        );
      });
    };
    if (isExpandedPreload) {
      // RN gives EACH idle callback a fresh 50ms deadline, not the time remaining in a
      // display frame. A return-to-Learn commit and both optional mounts can otherwise
      // run in one long burst. Let the ready three-surface deck reach a frame boundary
      // before each extra face. The second frame prevents admission in that first rAF's
      // pre-paint phase; actual mount work remains an interruptible idle transition.
      cancelFrames = safeRequestAnimationFrame(
        () => {
          if (!cancelled) {
            handle = requestIdleCallback(admit);
          }
        },
        "learn-surface-preloads",
        { frames: 2 },
      );
    } else {
      handle = requestIdleCallback(admit);
    }
    return () => {
      cancelled = true;
      if (handle !== undefined) {
        cancelIdleCallback(handle);
      }
      cancelFrames?.();
    };
  }, [isExpandedPreload, keysStamp, nextKey, precedingReady, state.transition.phase]);
  return (slot: LearnDeckSlot, face: "front" | "back") =>
    mountedKeys.includes(surfaceKey(slot, face));
}
